import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Tooltip } from '@eposnow/ui-core';
import { UIContext } from '../../../../context/UIContext';
import { SidekickTablesContext } from '../helpers/SidekickTablesContext';

interface ActionsMenuProps {
  objectId: number;
}

const ActionsMenu = ({ objectId }: ActionsMenuProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colorMode, isMobile } = useContext(UIContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const {
    handleAddClick,
    handleDuplicateClick,
    handleEditClick,
    handleDeleteClick,
    isLoading: isAdding,
    objectIdLoading,
  } = useContext(SidekickTablesContext);

  const isLoading = useMemo(
    () => isAdding && objectIdLoading === objectId,
    [isAdding, objectIdLoading]
  );

  return (
    <>
      <Tooltip
        title={
          isLoading
            ? t('screens.products.sidekick.import.productsTable.adding')
            : t('screens.products.sidekick.import.productsTable.openMenu')
        }
        theme={theme}
      >
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            color: 'text.secondary',
            ':hover': {
              color: 'text.primary',
              backgroundColor: colorMode === 'light' ? 'grey.4p' : 'elevation.4dp',
            },
          }}
          aria-label={
            isLoading
              ? t('screens.products.sidekick.import.productsTable.adding')
              : t('screens.products.sidekick.import.productsTable.openMenu')
          }
          data-qa-id="three-dots-menu"
        >
          {isLoading ? (
            <CircularProgress color="inherit" size={24} />
          ) : (
            <MoreVertIcon fontSize="medium" />
          )}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleAddClick(objectId);
          }}
          data-qa-id="actions-add"
        >
          <ListItemIcon>
            <AddIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary={t('screens.products.sidekick.import.productsTable.add')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleDuplicateClick(objectId);
          }}
          data-qa-id="actions-duplicate"
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary={t('screens.products.sidekick.import.productsTable.duplicate')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleEditClick(objectId);
          }}
          data-qa-id="actions-edit"
        >
          <ListItemIcon>
            <EditIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary={t('screens.products.sidekick.import.productsTable.edit')} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleDeleteClick(objectId);
          }}
          data-qa-id="actions-delete"
        >
          <ListItemIcon>
            <DeleteIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary={t('screens.products.sidekick.import.productsTable.delete')} />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ActionsMenu;
